import React, { useState, useEffect, useCallback } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Card, CardHeader, CardBody, Col, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const COLORS = {
  Critical:      "#7f1d1d",
  High:          "#dc2626",
  Medium:        "#facc15",
  Low:           "#16a34a",
  Informational: "#f97316",
};

const cardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const iconChip = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: `0.5px solid ${toRgba("#FF5B00", 0.30)}`,
  background: toRgba("#FF5B00", 0.10),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const iconGlyph = {
  color: "#FF5B00",
  fontSize: 18,
};

const SEVERITY_ORDER = ["Critical", "High", "Medium", "Low", "Informational"];

const getAuthHeaders = () => {
  const authUser = JSON.parse(sessionStorage.getItem("authUser"));
  return {
    "Content-Type": "application/json",
    ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
  };
};

function SeverityDistributionChart() {
  const [severityData, setSeverityData] = useState(null);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/vulnerabilities/stats`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        sessionStorage.removeItem("authUser");
        window.location.href = "/login";
        return;
      }

      const data = await response.json();

      if (data?.success) {
        // severity_breakdown: { Critical: 20, High: 80, Medium: 10, Low: 10 }
        const breakdown = data.severity_breakdown || {};

        // Build ordered labels/values — skip severities with 0 count
        const labels = [];
        const values = [];
        const bgColors = [];

        SEVERITY_ORDER.forEach((sev) => {
          const count = breakdown[sev] ?? data.stats?.[sev.toLowerCase()] ?? 0;
          if (count > 0) {
            labels.push(sev);
            values.push(count);
            bgColors.push(COLORS[sev] || "#9ca3af");
          }
        });

        setSeverityData({ labels, values, bgColors });
        setTotal(data.stats?.total_vulnerabilities ?? values.reduce((a, b) => a + b, 0));
      } else {
        setError("Failed to load severity data");
      }
    } catch (err) {
      console.error("Error fetching severity stats:", err);
      setError("Could not reach server");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = severityData
    ? {
        labels: severityData.labels,
        datasets: [
          {
            data: severityData.values,
            backgroundColor: severityData.bgColors,
            borderWidth: 0,
            hoverOffset: 10,
            cutout: "70%",
            radius: "80%",
          },
        ],
      }
    : null;

  const options = {
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: { size: 14, family: "Segoe UI, Inter, sans-serif" },
          color: "#9ca3af",
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: "#111827",
        titleColor: "#f9fafb",
        bodyColor: "#e5e7eb",
        borderColor: "rgba(255,255,255,0.08)",
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
            return `${context.label}: ${value} (${pct}%)`;
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <Col xl={6}>
      <Card style={cardShell}>
        <CardHeader className="d-flex justify-content-between align-items-center" style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{ ...iconChip, marginRight: 8 }}>
              <i className="ri-bar-chart-line" style={iconGlyph} />
            </span>
            Severity Distribution
          </h5>
          <button
            className="btn btn-sm"
            style={{ border: "1px solid rgba(0,0,0,0.15)", color: "#f97316" }}
            onClick={fetchData}
            disabled={loading}
          >
            <FeatherIcon icon="refresh-cw" size={16} />
          </button>
        </CardHeader>

        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner style={{ color: "#f97316" }} />
              <p className="text-muted mt-2">Loading distribution...</p>
            </div>
          ) : error ? (
            <div className="text-center py-5">
              <FeatherIcon icon="wifi-off" size={32} className="text-muted mb-2" />
              <p className="text-muted mb-2">{error}</p>
              <button className="btn btn-sm btn-outline-secondary" onClick={fetchData}>
                Retry
              </button>
            </div>
          ) : !chartData || total === 0 ? (
            <div className="text-center py-5">
              <FeatherIcon icon="shield" size={32} className="text-muted mb-2" />
              <p className="text-muted mb-0">No vulnerability data available</p>
            </div>
          ) : (
            <>
              <p className="text-muted mb-3 mt-1">
                Total Vulnerabilities: <strong>{total.toLocaleString()}</strong>
              </p>
              <div style={{ height: "400px" }}>
                <Doughnut data={chartData} options={options} />
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </Col>
  );
}

export default SeverityDistributionChart;
